// Small shared controls. A real sliding switch (track + knob) used for the
// boolean toggles (metronome, quantize, monitor, loop-first) so on/off reads
// at a glance, not just as a colour change. Plus a reusable confirm dialog for
// destructive actions.

import { createDialog } from "./dialog.ts";

export interface ConfirmOptions {
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

/** A promise-based confirm dialog. Resolves true if the user confirms. */
export function confirmAction(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = createDialog(opts.title);
    const p = document.createElement("p");
    p.className = "confirm__body";
    p.textContent = opts.body;
    const row = document.createElement("div");
    row.className = "confirm__actions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn btn--ghost btn--sm";
    cancel.textContent = opts.cancelLabel ?? "Cancel";
    const ok = document.createElement("button");
    ok.type = "button";
    ok.className = opts.danger ? "btn btn--sm btn--danger" : "btn btn--sm";
    ok.textContent = opts.confirmLabel ?? "Confirm";
    row.append(cancel, ok);
    dialog.body.append(p, row);
    document.body.append(dialog.element);

    let settled = false;
    const finish = (result: boolean): void => {
      if (settled) return;
      settled = true;
      dialog.close();
      dialog.element.remove();
      resolve(result);
    };
    cancel.addEventListener("click", () => finish(false));
    ok.addEventListener("click", () => finish(true));
    // Closing via the X / overlay / Escape counts as cancel.
    dialog.element.addEventListener("click", (e) => {
      if (e.target === dialog.element) finish(false);
    });
    dialog.open();
    ok.focus();
  });
}

export interface Switch {
  el: HTMLButtonElement;
  set(on: boolean): void;
}

export function createSwitch(
  label: string,
  initial: boolean,
  onChange: (on: boolean) => void,
  title?: string,
): Switch {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "switch";
  btn.setAttribute("role", "switch");
  if (title) btn.title = title;

  const text = document.createElement("span");
  text.className = "switch__label";
  text.textContent = label;

  const track = document.createElement("span");
  track.className = "switch__track";
  const knob = document.createElement("span");
  knob.className = "switch__knob";
  track.append(knob);

  btn.append(text, track);

  let on = initial;
  const apply = (): void => {
    btn.classList.toggle("is-on", on);
    btn.setAttribute("aria-checked", String(on));
  };
  apply();

  btn.addEventListener("click", () => {
    on = !on;
    apply();
    onChange(on);
  });

  return {
    el: btn,
    set(next) {
      on = next;
      apply();
    },
  };
}
